// SPDX-License-Identifier: AGPL-3.0-or-later

import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {TABLE_GAME_ICONS} from '@app/features/table_game/utils/TableGameIcons';
import {TABLE_GAME_NAME_DESCRIPTORS} from '@app/features/table_game/utils/TableGameMessages';
import * as TableGameLeaderboardCommands from '@app/features/table_game_leaderboard/commands/TableGameLeaderboardCommands';
import {LeaderboardList} from '@app/features/table_game_leaderboard/components/LeaderboardList';
import {LeagueBadge} from '@app/features/table_game_leaderboard/components/LeagueBadge';
import styles from '@app/features/table_game_leaderboard/components/TableGameLeaderboardModal.module.css';
import {TableGameRewardShowcase} from '@app/features/table_game_leaderboard/components/TableGameRewardShowcase';
import TableGameLeaderboards, {
	type LeaderboardPeriod,
	type LeaderboardScope,
	leaderboardKey,
} from '@app/features/table_game_leaderboard/state/TableGameLeaderboards';
import {
	LEADERBOARDS_DESCRIPTOR,
	LEAGUE_PROGRESS_DESCRIPTOR,
	MY_RANK_DESCRIPTOR,
	NO_RANKED_GAMES_DESCRIPTOR,
	PLACEMENT_LEFT_DESCRIPTOR,
	POINTS_DESCRIPTOR,
	RR_DESCRIPTOR,
	TABLE_GAME_LEAGUE_DESCRIPTORS,
} from '@app/features/table_game_leaderboard/utils/TableGameLeaderboardMessages';
import {leagueProgressPercent, seasonOf} from '@app/features/table_game_leaderboard/utils/TableGameLeaderboardView';
import {SegmentedTabs} from '@app/features/ui/segmented_tabs/SegmentedTabs';
import {
	isTableGameRankedKind,
	TABLE_GAME_RANKED_KINDS,
	type TableGameRankedKind,
} from '@fluxer/constants/src/TableGameConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import * as RadioGroup from '@radix-ui/react-radio-group';
import {observer} from 'mobx-react-lite';
import {type CSSProperties, type ReactNode, useEffect, useId, useMemo, useRef, useState} from 'react';

const SEASON_NAME_DESCRIPTOR = msg({
	message: '{month} season',
	comment:
		'Table game leaderboards: the monthly season under the heading of the leaderboards page. {month} is the month name, for example "October season".',
});
const DAYS_LEFT_DESCRIPTOR = msg({
	message: '{days, plural, one {# day left} other {# days left}}',
	comment: 'Table game leaderboards: whole days after today until the current season ends. {days} is a number.',
});
const LAST_DAY_DESCRIPTOR = msg({
	message: 'Last day',
	comment: 'Table game leaderboards: today is the last day of the current season.',
});
const FINAL_STANDINGS_DESCRIPTOR = msg({
	message: 'Final standings',
	comment: 'Table game leaderboards: the previous season has ended; its board no longer changes.',
});
const GAME_DESCRIPTOR = msg({message: 'Game', comment: 'Table game leaderboards: label of the game choice.'});
const PERIOD_DESCRIPTOR = msg({
	message: 'Period',
	comment: 'Table game leaderboards: label of the this/last season or week choice.',
});
const THIS_SEASON_DESCRIPTOR = msg({
	message: 'This season',
	comment: 'Table game leaderboards: the current monthly season.',
});
const LAST_SEASON_DESCRIPTOR = msg({
	message: 'Last season',
	comment: 'Table game leaderboards: the previous monthly season.',
});
const THIS_WEEK_DESCRIPTOR = msg({
	message: 'This week',
	comment: 'Table game leaderboards: the current week, Monday to Sunday (Istanbul time).',
});
const LAST_WEEK_DESCRIPTOR = msg({message: 'Last week', comment: 'Table game leaderboards: the previous week.'});
const YOUR_STANDING_DESCRIPTOR = msg({
	message: 'Your standing',
	comment: 'Table game leaderboards: heading of the section with my own league and place on the board.',
});
const NEXT_LEAGUE_PROGRESS_DESCRIPTOR = msg({
	message: 'Progress to the next league',
	comment: 'Table game leaderboards: accessible name of the bar that shows my progress toward the next league.',
});
const OUTSIDE_TOP_DESCRIPTOR = msg({
	message: 'Outside the top 10,000',
	comment: 'Table game leaderboards: my place when it is below the tracked top of the season board.',
});
const NOT_ON_BOARD_DESCRIPTOR = msg({
	message: 'Not on the board yet this season',
	comment: 'Table game leaderboards: I have a league but not enough ranked games this season to be listed.',
});
const HIDDEN_DESCRIPTOR = msg({
	message: 'You are hidden from the {productName}-wide boards. Change it in Privacy settings.',
	comment:
		'Table game leaderboards: my privacy setting (or my age) keeps me off the boards of all users. {productName} is the app name.',
});
const MY_WEEK_GAMES_DESCRIPTOR = msg({
	message: '{games, plural, one {# ranked game this week} other {# ranked games this week}}',
	comment: 'Table game leaderboards: how many ranked games I played in this community this week. {games} is a number.',
});
const NOT_ON_WEEK_BOARD_DESCRIPTOR = msg({
	message: 'Not on this week’s board yet',
	comment: 'Table game leaderboards: I have not played enough ranked games in this community this week.',
});

function SeasonLine({period, season}: {period: LeaderboardPeriod; season: number | null}) {
	const {i18n} = useLingui();
	const {year, month, daysLeft} = seasonOf(Date.now(), period, season);
	const monthName = new Intl.DateTimeFormat(i18n.locale, {month: 'long', timeZone: 'UTC'}).format(
		Date.UTC(year, month - 1, 1),
	);
	let status: string;
	if (daysLeft === null) status = i18n._(FINAL_STANDINGS_DESCRIPTOR);
	else if (daysLeft === 0) status = i18n._(LAST_DAY_DESCRIPTOR);
	else status = i18n._(DAYS_LEFT_DESCRIPTOR, {days: daysLeft});
	return (
		<p className={styles.season} data-flx="table-game-leaderboard.body.season">
			<span className={styles.seasonName}>{i18n._(SEASON_NAME_DESCRIPTOR, {month: monthName})}</span>
			<span className={styles.seasonStatus}>{status}</span>
		</p>
	);
}

function StandingSection({children, footer}: {children: ReactNode; footer?: ReactNode}) {
	const {i18n} = useLingui();
	const headingId = useId();
	return (
		<section className={styles.standing} aria-labelledby={headingId} data-flx="table-game-leaderboard.body.my-card">
			<h2 id={headingId} className={styles.sectionTitle}>
				{i18n._(YOUR_STANDING_DESCRIPTOR)}
			</h2>
			{children}
			{footer}
		</section>
	);
}

const MyGlobalCard = observer(function MyGlobalCard({game}: {game: TableGameRankedKind}) {
	const {i18n} = useLingui();
	const mine = TableGameLeaderboards.myRatings;
	if (!mine) return null;
	const rating = TableGameLeaderboards.getMyRating(game);
	const hidden = !mine.listed && (
		<p className={styles.standingHint}>{i18n._(HIDDEN_DESCRIPTOR, {productName: PRODUCT_NAME})}</p>
	);
	if (!rating || rating.games === 0 || !rating.league) {
		return (
			<StandingSection footer={hidden}>
				<p className={styles.standingNote}>
					{!rating || rating.games === 0
						? i18n._(NO_RANKED_GAMES_DESCRIPTOR)
						: i18n._(PLACEMENT_LEFT_DESCRIPTOR, {count: rating.placement_left})}
				</p>
			</StandingSection>
		);
	}
	const percent = rating.progress !== null ? leagueProgressPercent(rating.progress) : null;
	return (
		<StandingSection footer={hidden}>
			<div className={styles.standingMain}>
				<LeagueBadge league={rating.league} className={styles.standingBadge} />
				<div className={styles.standingBody}>
					<span className={styles.standingLeague}>{i18n._(TABLE_GAME_LEAGUE_DESCRIPTORS[rating.league])}</span>
					{rating.rank !== null && (
						<span className={styles.standingPlace}>{i18n._(MY_RANK_DESCRIPTOR, {rank: rating.rank})}</span>
					)}
					{rating.rr !== null && (
						<span className={styles.standingDetail}>{i18n._(RR_DESCRIPTOR, {rr: rating.rr})}</span>
					)}
					{percent !== null && (
						<span className={styles.progress}>
							<span
								className={styles.progressTrack}
								role="progressbar"
								aria-valuemin={0}
								aria-valuemax={100}
								aria-valuenow={percent}
								aria-label={i18n._(NEXT_LEAGUE_PROGRESS_DESCRIPTOR)}
							>
								<span className={styles.progressFill} style={{'--progress': percent / 100} as CSSProperties} />
							</span>
							<span className={styles.progressValue}>{i18n._(LEAGUE_PROGRESS_DESCRIPTOR, {percent})}</span>
						</span>
					)}
				</div>
			</div>
			{rating.rank === null && (
				<p className={styles.standingNote}>
					{i18n._(rating.qualified ? OUTSIDE_TOP_DESCRIPTOR : NOT_ON_BOARD_DESCRIPTOR)}
				</p>
			)}
		</StandingSection>
	);
});

const MyGuildCard = observer(function MyGuildCard({boardKey}: {boardKey: string}) {
	const {i18n} = useLingui();
	const me = TableGameLeaderboards.getBoard(boardKey)?.me;
	if (!me) return null;
	return (
		<StandingSection>
			<div className={styles.standingBody}>
				<span className={styles.standingLeague}>{i18n._(POINTS_DESCRIPTOR, {points: me.points})}</span>
				{me.rank !== null && (
					<span className={styles.standingPlace}>{i18n._(MY_RANK_DESCRIPTOR, {rank: me.rank})}</span>
				)}
				<span className={styles.standingDetail}>{i18n._(MY_WEEK_GAMES_DESCRIPTOR, {games: me.games})}</span>
			</div>
			{me.rank === null && <p className={styles.standingNote}>{i18n._(NOT_ON_WEEK_BOARD_DESCRIPTOR)}</p>}
		</StandingSection>
	);
});

// The leaderboards themselves, shared by the modal and the page: the game and period choice, my standing and the
// list. It shares the leaderboard store while mounted: the last body to unmount drops every board and my ratings.
// With guildId it shows that community's weekly board, else the season board of the chosen game. The page shows its
// own heading with the season, the season rewards and, on a wide screen, a column beside the board; the modal already
// has a heading in its header.
export const TableGameLeaderboardBody = observer(function TableGameLeaderboardBody({
	guildId,
	initialGame = TABLE_GAME_RANKED_KINDS[0],
	heading = false,
}: {
	guildId: string | null;
	initialGame?: TableGameRankedKind;
	heading?: boolean;
}) {
	const {i18n} = useLingui();
	const [game, setGame] = useState<TableGameRankedKind>(initialGame);
	const [period, setPeriod] = useState<LeaderboardPeriod>('current');
	const pickerRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const release = TableGameLeaderboards.acquire();
		void TableGameLeaderboardCommands.loadMyRatings();
		if (heading) void TableGameLeaderboardCommands.loadShowcase();
		return release;
	}, [heading]);
	// The tiles scroll sideways when they do not fit: bring the game it opened on into view once.
	useEffect(() => {
		pickerRef.current?.querySelector('[data-state="checked"]')?.scrollIntoView?.({block: 'nearest', inline: 'nearest'});
	}, []);
	const scope = useMemo<LeaderboardScope>(
		() => (guildId ? {kind: 'guild', guildId, period} : {kind: 'global', game, period}),
		[guildId, game, period],
	);
	const periods =
		scope.kind === 'guild'
			? [
					{id: 'current' as const, label: i18n._(THIS_WEEK_DESCRIPTOR)},
					{id: 'previous' as const, label: i18n._(LAST_WEEK_DESCRIPTOR)},
				]
			: [
					{id: 'current' as const, label: i18n._(THIS_SEASON_DESCRIPTOR)},
					{id: 'previous' as const, label: i18n._(LAST_SEASON_DESCRIPTOR)},
				];
	const periodTabs = (
		<SegmentedTabs
			tabs={periods}
			selectedTab={period}
			onTabChange={setPeriod}
			ariaLabel={i18n._(PERIOD_DESCRIPTOR)}
			className={styles.segmented}
			data-flx="table-game-leaderboard.body.period"
		/>
	);
	const myCard =
		scope.kind === 'global' ? (
			period === 'current' && <MyGlobalCard game={game} data-flx="table-game-leaderboard.body.my-global-card" />
		) : (
			<MyGuildCard boardKey={leaderboardKey(scope)} data-flx="table-game-leaderboard.body.my-guild-card" />
		);
	const showcase = heading && scope.kind === 'global' && period === 'current' && (
		<TableGameRewardShowcase game={game} />
	);
	return (
		<div className={styles.layout} data-page={heading || undefined} data-flx="table-game-leaderboard.body.layout">
			<div className={styles.controls} data-flx="table-game-leaderboard.body.controls">
				{heading ? (
					<div className={styles.header} data-flx="table-game-leaderboard.body.header">
						<div className={styles.headerText} data-flx="table-game-leaderboard.body.header-text">
							<h1 className={styles.title} data-flx="table-game-leaderboard.body.title">
								{i18n._(LEADERBOARDS_DESCRIPTOR)}
							</h1>
							{scope.kind === 'global' && (
								<SeasonLine
									period={period}
									season={TableGameLeaderboards.getBoard(leaderboardKey(scope))?.season ?? null}
								/>
							)}
						</div>
						{periodTabs}
					</div>
				) : (
					periodTabs
				)}
				{scope.kind === 'global' && (
					<RadioGroup.Root
						ref={pickerRef}
						className={styles.gamePicker}
						value={game}
						onValueChange={(value) => isTableGameRankedKind(value) && setGame(value)}
						orientation="horizontal"
						aria-label={i18n._(GAME_DESCRIPTOR)}
						data-flx="table-game-leaderboard.body.game-picker"
					>
						{TABLE_GAME_RANKED_KINDS.map((id) => (
							<RadioGroup.Item
								key={id}
								value={id}
								className={styles.gameCard}
								data-game-choice={id}
								data-flx="table-game-leaderboard.body.game-card"
							>
								<img
									src={TABLE_GAME_ICONS[id]}
									alt=""
									width={256}
									height={256}
									loading="lazy"
									decoding="async"
									draggable={false}
									className={styles.gameIcon}
									data-flx="table-game-leaderboard.body.game-icon"
								/>
								<span className={styles.gameName} data-flx="table-game-leaderboard.body.game-name">
									{i18n._(TABLE_GAME_NAME_DESCRIPTORS[id])}
								</span>
							</RadioGroup.Item>
						))}
					</RadioGroup.Root>
				)}
			</div>
			<div className={styles.main} data-flx="table-game-leaderboard.body.main">
				<div className={styles.aside} data-flx="table-game-leaderboard.body.aside">
					{myCard}
					{showcase}
				</div>
				<LeaderboardList
					key={leaderboardKey(scope)}
					scope={scope}
					podium={heading}
					className={styles.list}
					data-flx="table-game-leaderboard.body.leaderboard-list"
				/>
			</div>
		</div>
	);
});
